import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Header from '../components/header';
import DefaultFooter from '../components/default-footer';
import { API_URL } from '../config';

// ============================================================
// CruiseHolidays.tsx
// Destinations section now driven by real activities from the API.
// ============================================================

type IconName = 'check' | 'clock' | 'shield' | 'user' | 'doc' | 'map' | 'star' | 'phone' | 'globe' | 'route' | 'bed' | 'window' | 'balcony' | 'crown' | 'arrow' | 'car' | 'plane';

interface Highlight {
  icon: IconName;
  title: string;
  description: string;
}

interface ActivityPhoto {
  id: number;
  path: string;
  url: string;
}

interface ActivityApi {
  id: number;
  name: string;
  description: string;
  price: string;
  inclusions: string[];
  itinerary: Record<string, string>;
  photos: ActivityPhoto[];
}

interface CabinTier {
  icon: IconName;
  name: string;
  description: string;
  bestFor: string;
}

interface Step {
  n: string;
  title: string;
  description: string;
}

interface Faq {
  q: string;
  a: string;
}

interface DepartureLocation {
  name: string;
  time: string;
  mode: 'car' | 'plane';
}

const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1606813332135-228593b6e201?w=1800&q=80&fit=crop&crop=center',
  split: 'https://images.unsplash.com/photo-1749465139134-4769e39b7db4?w=1200&q=80&fit=crop&crop=center',
  fallback: 'https://images.unsplash.com/photo-1677151420167-b2de389d02db?w=1200&q=80&fit=crop&crop=center',
};

const highlights: Highlight[] = [
  { icon: 'map', title: "Region & Line Matched to You", description: "A quick Gulf sailing from Dubai or a fly-cruise via Muscat International — whichever fits your dates." },
  { icon: 'star', title: "Shore Excursions Pre-Booked", description: "Popular stops sell out; yours are secured before departure." },
  { icon: 'shield', title: "Dietary & Accessibility Requests", description: "Special requirements passed to the cruise line in advance." },
  { icon: 'route', title: "Flights & Pre-Cruise Hotels", description: "Muscat departures, transfers, and stays either side of the sailing arranged as one trip." }
];

const departureLocations: DepartureLocation[] = [
  { name: 'Dubai', time: '2.5 hrs drive', mode: 'car' },
  { name: 'Abu Dhabi', time: '3.5 hrs drive', mode: 'car' },
  { name: 'Sharjah', time: '2 hrs drive', mode: 'car' },
  { name: 'RAK', time: '1.5 hrs drive', mode: 'car' },
  { name: 'Fujairah', time: '2.5 hrs drive', mode: 'car' },
  { name: 'Muscat', time: 'Flight', mode: 'plane' },
];

const cabinTiers: CabinTier[] = [
  { icon: 'window', name: 'Interior', description: 'No window, lowest fare, quietest sleep — the pick for anyone spending most of the day on deck anyway.', bestFor: 'Budget-first cruisers' },
  { icon: 'bed', name: 'Ocean View', description: 'A fixed porthole or window brings in daylight and a horizon check without the balcony premium.', bestFor: 'First-time cruisers' },
  { icon: 'balcony', name: 'Balcony', description: 'Private outdoor space to watch glaciers, coastlines, and sail-aways — the most requested upgrade on scenic routes.', bestFor: 'Alaska & Fjords sailings' },
  { icon: 'crown', name: 'Suite', description: 'Separate living space, priority boarding, and often a dedicated concierge or butler service.', bestFor: 'Anniversaries & special occasions' },
];

const steps: Step[] = [
  { n: '01', title: "Tell Us Your Dates & Region", description: "Preferred cruise line, length of trip, and rough budget." },
  { n: '02', title: "We Shortlist Lines & Cabins", description: "Two or three real options, with the trade-offs explained plainly." },
  { n: '03', title: "Booking Confirmed With Excursions", description: "Cabin, dining time, and shore excursions locked in together." },
  { n: '04', title: "Boarding Pack Sent", description: "Documents, luggage tags, Muscat International flight details, and port timing sent before departure." }
];

const faqs: Faq[] = [
  { q: "Are gratuities included in the price?", a: "This varies by cruise line — we'll tell you exactly what's included and what's charged onboard before you book." },
  { q: "What if I'm prone to seasickness?", a: "We can steer you toward a cabin position — lower deck, midship — that generally feels the least motion, and mention itineraries with calmer waters." },
  { q: "Do you arrange cruises for solo travelers?", a: "Yes, including flagging which cruise lines offer solo cabins without a full double-occupancy surcharge." },
  { q: "How far in advance should I book?", a: "For popular routes like the Mediterranean in peak summer, 9–12 months ahead secures the best cabin selection and fare. Alaska and the Fjords sell out even earlier for balcony cabins." },
  { q: "Can I combine two cruise lines or add a land stay?", a: "Yes — a common request is a few nights in the embarkation city before sailing, or flying home from the final port rather than backtracking. We build it as one itinerary." }
];

function DuneRule() {
  return (
    <div aria-hidden="true" style={{ lineHeight: 0 }}>
      <svg viewBox="0 0 1200 34" width="100%" height="22" preserveAspectRatio="none">
        <path d="M0 24 C 90 4, 170 4, 250 20 S 400 30, 480 14 S 620 2, 700 18 S 850 30, 940 12 S 1090 2, 1200 16" fill="none" stroke="var(--gold-d)" strokeOpacity="0.55" strokeWidth="1.5" />
        <path d="M0 28 C 90 12, 170 12, 250 26 S 400 34, 480 20 S 620 10, 700 24 S 850 34, 940 18 S 1090 10, 1200 22" fill="none" stroke="rgba(41,75,90,.14)" strokeWidth="1" />
      </svg>
    </div>
  );
}

function Icon({ name }: { name: IconName }) {
  const common = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (name) {
    case 'check':
      return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M8.5 12.5l2.5 2.5 4.5-5" /></svg>;
    case 'clock':
      return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.2 3.2" /></svg>;
    case 'shield':
      return <svg {...common}><path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" /></svg>;
    case 'user':
      return <svg {...common}><circle cx="12" cy="8" r="3" /><path d="M5 21c0-4 3-6.5 7-6.5S19 17 19 21" /></svg>;
    case 'doc':
      return <svg {...common}><rect x="5" y="3" width="14" height="18" rx="1.5" /><path d="M9 8h6M9 12h6M9 16h4" /></svg>;
    case 'map':
      return <svg {...common}><path d="M9 20l-5-2V6l5 2 6-2 5 2v12l-5-2-6 2z" /><path d="M9 6v14M15 4v14" /></svg>;
    case 'star':
      return <svg {...common}><path d="M12 3l2.6 5.7 6.2.6-4.7 4.2 1.4 6.1L12 16.9 6.5 19.6l1.4-6.1L3.2 9.3l6.2-.6L12 3z" /></svg>;
    case 'phone':
      return <svg {...common}><rect x="7" y="2" width="10" height="20" rx="2" /><path d="M11 18h2" /></svg>;
    case 'globe':
      return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.5 2.5 15.5 0 18M12 3c-2.5 2.5-2.5 15.5 0 18" /></svg>;
    case 'route':
      return <svg {...common}><circle cx="6" cy="6" r="2.3" /><circle cx="18" cy="18" r="2.3" /><path d="M6 8.3V13a3 3 0 0 0 3 3h6" /></svg>;
    case 'bed':
      return <svg {...common}><path d="M3 18v-6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v6" /><path d="M3 18v2M21 18v2" /><path d="M3 12v-2a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v2" /></svg>;
    case 'window':
      return <svg {...common}><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M12 4v16M4 12h16" /></svg>;
    case 'balcony':
      return <svg {...common}><path d="M4 21V9l8-5 8 5v12" /><path d="M4 15h16" /><path d="M9 21v-4M15 21v-4" /></svg>;
    case 'crown':
      return <svg {...common}><path d="M4 18h16l-1.5-9-4 3.5L12 5l-2.5 7.5-4-3.5L4 18z" /></svg>;
    case 'arrow':
      return <svg {...common}><path d="M5 12h14M13 6l6 6-6 6" /></svg>;
    case 'car':
      return <svg {...common}><path d="M4 16V11l2-5h12l2 5v5" /><circle cx="7.5" cy="16.5" r="1.6" /><circle cx="16.5" cy="16.5" r="1.6" /></svg>;
    case 'plane':
      return <svg {...common}><path d="M3 13l7-2 4-8 2 1-2 8 6 2-1 2-6-1-3 5-2-1 1-5-6-1z" /></svg>;
    default:
      return null;
  }
}

// ── Skeleton pour une carte activité pendant le chargement ────────
function ActivityCardSkeleton() {
  return (
    <div className="activity-card">
      <div className="skel" style={{ height: 210, borderRadius: 0 }} />
      <div className="activity-card-body">
        <div className="skel" style={{ width: '90%', height: 14, marginBottom: 10 }} />
        <div className="skel" style={{ width: '70%', height: 14, marginBottom: 18 }} />
        <div className="skel" style={{ width: '100%', height: 40, borderRadius: 6 }} />
      </div>
    </div>
  );
}

export default function CruiseHolidays() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const [activities, setActivities] = useState<ActivityApi[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [activitiesError, setActivitiesError] = useState(false);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const res = await axios.get(`${API_URL}/activities-list`);
        setActivities(res.data.data || []);
      } catch (err) {
        console.error('Error fetching activities:', err);
        setActivitiesError(true);
      } finally {
        setLoadingActivities(false);
      }
    };
    fetchActivities();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.3 }
    );
    const statItems = document.querySelectorAll('.stat-item');
    statItems.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        :root {
          --teal: #294b5a;
          --teal-d: #1a3140;
          --teal-l: #3b6272;
          --gold: #d3be76;
          --gold-l: #ede4b5;
          --gold-d: #b09a50;
          --cream: #f7f4ee;
          --mist: #edeae2;
          --silver: #8a9aa3;
          --ink: #1a2f38;
          --radius: 10px;
          --shadow-sm: 0 6px 20px rgba(26, 49, 61, .07);
          --shadow-md: 0 20px 48px rgba(26, 49, 61, .12);
          --shadow-lg: 0 28px 64px rgba(26, 49, 61, .18);
        }
        * { box-sizing: border-box; }
        .visa-page { font-family: 'DM Sans', sans-serif; background: var(--cream); -webkit-font-smoothing: antialiased; }
        .visa-page h1, .visa-page h2, .visa-page h3 { font-family: 'Playfair Display', serif; }
        .visa-page a:focus-visible, .visa-page button:focus-visible { outline: 2px solid var(--gold-d); outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) { .visa-page *, .visa-page *::before, .visa-page *::after { animation-duration: .01ms !important; transition-duration: .01ms !important; } }

        .eyebrow { display: inline-flex; align-items: center; gap: 10px; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-d); }
        .eyebrow::before { content: ''; width: 22px; height: 2px; background: var(--gold); border-radius: 2px; }
        .eyebrow.light { color: var(--gold-l); }
        .eyebrow.light::before { background: var(--gold-l); opacity: .6; }
        .eyebrow.centered { justify-content: center; }

        .section-title { font-size: clamp(1.7rem, 2.6vw, 2.4rem); font-weight: 500; color: var(--ink); line-height: 1.2; margin: 10px 0 12px; }
        .section-title.centered { text-align: center; }
        .section-sub { font-size: 15px; color: var(--silver); font-weight: 300; line-height: 1.7; max-width: 560px; }
        .section-sub.centered { margin: 0 auto; text-align: center; }
        .section-pad { padding: 84px 0; }
        @media (max-width: 991px) { .section-pad { padding: 64px 0; } }
        @media (max-width: 576px) { .section-pad { padding: 48px 0; } }
        .bg-cream { background: var(--cream); }
        .bg-white { background: #fff; }
        .bg-teal { background: var(--teal-d); }

        .vbtn-primary { display: inline-flex; align-items: center; gap: 8px; background: var(--gold); color: var(--teal-d); border-radius: 5px; font-size: 12.5px; font-weight: 700; letter-spacing: .3px; padding: 14px 26px; text-decoration: none; transition: transform .2s, background .2s; box-shadow: 0 10px 28px rgba(211,190,118,.28); border: none; cursor: pointer; }
        .vbtn-primary:hover { background: #e2ce86; transform: translateY(-2px); }
        .vbtn-ghost { display: inline-flex; align-items: center; background: rgba(255,255,255,.1); backdrop-filter: blur(4px); color: #fff; border: 1px solid rgba(255,255,255,.25); border-radius: 5px; font-size: 12.5px; font-weight: 600; letter-spacing: .3px; padding: 14px 24px; text-decoration: none; transition: background .2s, border-color .2s; }
        .vbtn-ghost:hover { border-color: #fff; background: rgba(255,255,255,.18); }

        /* HERO */
        .svc-hero { position: relative; min-height: 500px; display: flex; align-items: center; overflow: hidden; background: var(--teal-d); }
        .svc-hero-img { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: 0.34; }
        .svc-hero-veil { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(26,49,61,.55) 0%, rgba(26,49,61,.92) 100%); }
        .svc-hero-inner { position: relative; z-index: 2; padding: 64px 0 40px; }
        .svc-hero-title { font-weight: 600; color: #fff; line-height: 1.16; font-size: clamp(30px, 4vw, 48px); max-width: 660px; margin-bottom: 16px; }
        .svc-hero-title .gold { font-style: italic; color: var(--gold); font-weight: 400; }
        .svc-hero-sub { font-size: 15px; color: rgba(255,255,255,.78); font-weight: 300; line-height: 1.75; max-width: 540px; margin-bottom: 30px; }
        .svc-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; }
        .svc-breadcrumb { font-size: 11.5px; color: rgba(255,255,255,.55); margin-bottom: 18px; }
        .svc-breadcrumb a { color: rgba(255,255,255,.75); text-decoration: none; }
        .svc-breadcrumb a:hover { color: var(--gold-l); }

        /* STATS */
        .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 30px; }
        @media (max-width: 768px) { .stats-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 480px) { .stats-grid { grid-template-columns: 1fr; } }
        .stat-item { text-align: center; padding: 20px; opacity: 0; transform: translateY(20px); transition: opacity .8s ease, transform .8s ease; }
        .stat-item.visible { opacity: 1; transform: translateY(0); }
        .stat-item .value { font-family: 'Playfair Display', serif; font-size: 2.6rem; font-weight: 600; color: var(--gold); line-height: 1.1; }
        .stat-item .label { font-size: 13px; color: rgba(255,255,255,.7); font-weight: 300; margin-top: 4px; }

        /* HIGHLIGHTS */
        .highlight-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; background: rgba(41,75,90,.08); border: 1px solid rgba(41,75,90,.08); border-radius: var(--radius); overflow: hidden; }
        @media (max-width: 900px) { .highlight-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 520px) { .highlight-grid { grid-template-columns: 1fr; } }
        .highlight-cell { background: #fff; padding: 28px 24px; }
        .highlight-cell .hl-icon { width: 42px; height: 42px; border-radius: 50%; background: var(--cream); color: var(--gold-d); display: flex; align-items: center; justify-content: center; margin-bottom: 16px; }
        .highlight-cell h3 { font-size: 14.5px; font-weight: 600; color: var(--ink); margin-bottom: 6px; }
        .highlight-cell p { font-size: 12.5px; color: var(--silver); font-weight: 300; line-height: 1.65; margin: 0; }

        /* ACTIVITIES GRID (was regions) */
        .activity-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; }
        @media (max-width: 992px) { .activity-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 640px) { .activity-grid { grid-template-columns: 1fr; } }
        .activity-card { background: #fff; border-radius: var(--radius); overflow: hidden; box-shadow: var(--shadow-sm); border: 1px solid rgba(41,75,90,.06); transition: transform .4s cubic-bezier(.22,1,.36,1), box-shadow .4s cubic-bezier(.22,1,.36,1); display: flex; flex-direction: column; }
        .activity-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-md); }
        .activity-card-img { height: 210px; background-size: cover; background-position: center; position: relative; }
        .activity-card-img::after { content: ''; position: absolute; inset: 0; background: linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,.55) 100%); }
        .activity-card-img .price-badge { position: absolute; top: 14px; right: 14px; background: rgba(26,49,61,.85); backdrop-filter: blur(8px); padding: 5px 12px; border-radius: 20px; color: var(--gold-l); font-size: 11px; font-weight: 700; letter-spacing: .3px; border: 1px solid rgba(255,255,255,.12); z-index: 2; }
        .activity-card-img .photo-count { position: absolute; bottom: 14px; right: 14px; background: rgba(0,0,0,.5); color: #fff; font-size: 10.5px; padding: 3px 9px; border-radius: 20px; z-index: 2; }
        .activity-card-img .name-badge { position: absolute; bottom: 14px; left: 14px; right: 70px; z-index: 2; }
        .activity-card-img .a-name { font-family: 'Playfair Display', serif; font-size: 1.15rem; font-weight: 600; color: #fff; text-shadow: 0 2px 10px rgba(0,0,0,.4); display: block; line-height: 1.25; }
        .activity-card-body { padding: 20px 22px 22px; display: flex; flex-direction: column; flex: 1; }
        .activity-card-body p { font-size: 13px; color: var(--silver); font-weight: 300; line-height: 1.65; margin: 0 0 14px; flex: 1; }
        .activity-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 16px; }
        .activity-tags span { font-size: 10.5px; color: var(--teal); font-weight: 500; background: var(--cream); border: 1px solid rgba(41,75,90,.1); border-radius: 20px; padding: 4px 10px; }
        .activity-book-btn { display: inline-flex; align-items: center; justify-content: center; gap: 7px; width: 100%; background: var(--teal); color: #fff; border: none; border-radius: 6px; font-size: 12.5px; font-weight: 700; letter-spacing: .2px; padding: 12px 18px; text-decoration: none; cursor: pointer; transition: background .2s ease, transform .2s ease; }
        .activity-book-btn:hover { background: var(--teal-d); transform: translateY(-2px); color: #fff; }
        .activity-book-btn svg { width: 15px; height: 15px; }

        .activity-empty, .activity-error { text-align: center; padding: 60px 20px; color: var(--silver); font-size: 14px; background: #fff; border-radius: var(--radius); border: 1px dashed rgba(41,75,90,.15); }
        .activity-error { color: #b3453f; }

        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        .skel { background: linear-gradient(90deg, #ece8de 25%, #e0dccf 50%, #ece8de 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 6px; }

        /* DEPARTING FROM */
        .location-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
        @media (max-width: 900px) { .location-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 560px) { .location-grid { grid-template-columns: 1fr; } }
        .location-card { display: flex; align-items: center; gap: 16px; background: #fff; border: 1px solid rgba(41,75,90,.08); border-radius: var(--radius); padding: 22px 22px; transition: border-color .3s ease, box-shadow .3s ease, transform .3s ease; }
        .location-card:hover { border-color: rgba(211,190,118,.55); box-shadow: var(--shadow-sm); transform: translateY(-3px); }
        .location-card .loc-icon { width: 46px; height: 46px; border-radius: 50%; background: var(--cream); color: var(--gold-d); display: flex; align-items: center; justify-content: center; flex: none; }
        .location-card .loc-name { font-family: 'Playfair Display', serif; font-size: 1.05rem; font-weight: 600; color: var(--ink); }
        .location-card .loc-time { font-size: 12px; color: var(--silver); font-weight: 300; margin-top: 2px; }

        /* CABIN TIERS */
        .cabin-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; }
        @media (max-width: 900px) { .cabin-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 520px) { .cabin-grid { grid-template-columns: 1fr; } }
        .cabin-card { background: #fff; border-radius: var(--radius); padding: 30px 24px; border: 1px solid rgba(41,75,90,.08); text-align: left; transition: border-color .3s ease, box-shadow .3s ease, transform .3s ease; }
        .cabin-card:hover { border-color: rgba(211,190,118,.55); box-shadow: var(--shadow-sm); transform: translateY(-3px); }
        .cabin-card .ci-icon { width: 46px; height: 46px; border-radius: 50%; background: var(--teal-d); color: var(--gold-l); display: flex; align-items: center; justify-content: center; margin-bottom: 18px; }
        .cabin-card h3 { font-size: 1.05rem; font-weight: 600; color: var(--ink); margin-bottom: 8px; }
        .cabin-card p { font-size: 12.5px; color: var(--silver); font-weight: 300; line-height: 1.65; margin: 0 0 14px; }
        .cabin-card .best-for { font-size: 10.5px; letter-spacing: .4px; text-transform: uppercase; color: var(--gold-d); font-weight: 700; }

        /* PROCESS */
        .rituals-rail { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; position: relative; }
        @media (max-width: 900px) { .rituals-rail { grid-template-columns: 1fr 1fr; gap: 28px; } }
        @media (max-width: 560px) { .rituals-rail { grid-template-columns: 1fr; } }
        .rituals-rail::before { content: ''; position: absolute; top: 26px; left: 6%; right: 6%; height: 1px; background: repeating-linear-gradient(90deg, var(--gold-d) 0 8px, transparent 8px 16px); opacity: .5; }
        @media (max-width: 900px) { .rituals-rail::before { display: none; } }
        .ritual-card { position: relative; padding: 0 22px; text-align: left; }
        .ritual-card:first-child { padding-left: 0; }
        .ritual-num { position: relative; z-index: 1; width: 52px; height: 52px; border-radius: 50%; background: var(--teal-d); color: var(--gold-l); font-family: 'Playfair Display', serif; font-size: 1.05rem; font-weight: 600; display: flex; align-items: center; justify-content: center; margin-bottom: 18px; border: 1px solid var(--gold-d); }
        .ritual-card h3 { font-size: 1.02rem; font-weight: 600; color: var(--ink); margin-bottom: 6px; }
        .ritual-card p { font-size: 13px; color: var(--silver); font-weight: 300; line-height: 1.7; margin: 0; }

        /* FAQ */
        .faq-list { max-width: 760px; margin: 0 auto; }
        .faq-item { border-bottom: 1px solid rgba(41,75,90,.1); }
        .faq-item:first-child { border-top: 1px solid rgba(41,75,90,.1); }
        .faq-q { width: 100%; background: none; border: none; text-align: left; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 22px 4px; font-family: 'Playfair Display', serif; font-size: 1.02rem; font-weight: 500; color: var(--ink); }
        .faq-q .plus { flex: none; width: 22px; height: 22px; border-radius: 50%; border: 1.5px solid var(--gold-d); position: relative; transition: transform .3s ease, background .3s ease; }
        .faq-q .plus::before, .faq-q .plus::after { content: ''; position: absolute; background: var(--gold-d); top: 50%; left: 50%; transform: translate(-50%,-50%); }
        .faq-q .plus::before { width: 9px; height: 1.4px; }
        .faq-q .plus::after { width: 1.4px; height: 9px; transition: opacity .2s ease; }
        .faq-item.open .plus { background: var(--gold-d); transform: rotate(180deg); }
        .faq-item.open .plus::before, .faq-item.open .plus::after { background: #fff; }
        .faq-item.open .plus::after { opacity: 0; }
        .faq-a { max-height: 0; overflow: hidden; transition: max-height .35s ease; }
        .faq-item.open .faq-a { max-height: 280px; }
        .faq-a p { font-size: 13.5px; color: var(--silver); font-weight: 300; line-height: 1.75; padding: 0 4px 24px; max-width: 640px; }

        /* CTA BANNER */
        .cta-banner { background: var(--teal); border-radius: var(--radius); padding: 44px 40px; display: flex; align-items: center; justify-content: space-between; gap: 30px; flex-wrap: wrap; }
        .cta-banner-title { color: #fff; font-size: 1.3rem; font-weight: 500; margin-bottom: 4px; }
        .cta-banner-sub { color: rgba(255,255,255,.7); font-size: 13px; font-weight: 300; max-width: 460px; }

        @media (max-width: 576px) {
          .cta-banner { padding: 28px 20px; }
          .ritual-card { padding: 0; }
          .activity-card-img { height: 180px; }
        }
`}</style>

      <div className="visa-page">
        <Header />

        {/* ===== HERO ===== */}
        <section className="svc-hero">
          <div className="svc-hero-img" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
          <div className="svc-hero-veil" />
          <div className="container svc-hero-inner">
            <div className="svc-breadcrumb"><a href="/services">Services</a> / Cruise Holidays</div>
            <div className="eyebrow light" style={{ marginBottom: 14 }}>
              Based in Muscat, Sultanate of Oman
            </div>
            <h1 className="svc-hero-title">
              Cruise Holidays, <span className="gold">From Muscat to the World</span>
            </h1>
            <p className="svc-hero-sub">
              From a weekend Gulf sailing out of Dubai to a fly-cruise through the Mediterranean via Muscat International — cabin category matched to your budget, shore excursions booked before you sail, and every flight and transfer coordinated from home.
            </p>
            <div className="svc-hero-ctas">
              <a href="/contact" className="vbtn-primary">Get a Quote</a>
              <a href="#destinations" className="vbtn-ghost">Explore Activities</a>
            </div>
          </div>
        </section>

        {/* ===== HIGHLIGHTS ===== */}
        <section className="section-pad bg-white">
          <div className="container">
            <div style={{ marginBottom: 44 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                What You Get
              </span>
              <h2 className="section-title centered">Built Around the Details That Matter</h2>
            </div>
            <div className="highlight-grid">
              {highlights.map((h) => (
                <div className="highlight-cell" key={h.title}>
                  <div className="hl-icon"><Icon name={h.icon} /></div>
                  <h3>{h.title}</h3>
                  <p>{h.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== ACTIVITIES (from API) ===== */}
        <section className="section-pad bg-cream" id="destinations">
          <div className="container">
            <div style={{ marginBottom: 44 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                Where To Sail
              </span>
              <h2 className="section-title centered">Available Activities & Excursions</h2>
              <p className="section-sub centered">
                Real trips we run right now — pick one and we'll take care of the rest.
              </p>
            </div>

            {loadingActivities && (
              <div className="activity-grid">
                {[...Array(3)].map((_, i) => <ActivityCardSkeleton key={i} />)}
              </div>
            )}

            {!loadingActivities && activitiesError && (
              <div className="activity-error">
                Couldn't load activities right now. Please refresh the page or try again shortly.
              </div>
            )}

            {!loadingActivities && !activitiesError && activities.length === 0 && (
              <div className="activity-empty">
                No activities available at the moment. Check back soon.
              </div>
            )}

            {!loadingActivities && !activitiesError && activities.length > 0 && (
              <div className="activity-grid">
                {activities.map((activity) => (
                  <div className="activity-card" key={activity.id}>
                    <div
                      className="activity-card-img"
                      style={{ backgroundImage: `url(${activity.photos?.[0]?.url || IMAGES.fallback})` }}
                    >
                      <span className="price-badge">{activity.price} OMR</span>
                      {activity.photos && activity.photos.length > 1 && (
                        <span className="photo-count">+{activity.photos.length - 1} photos</span>
                      )}
                      <div className="name-badge">
                        <span className="a-name">{activity.name}</span>
                      </div>
                    </div>
                    <div className="activity-card-body">
                      <p>{activity.description}</p>

                      {activity.inclusions && activity.inclusions.length > 0 && (
                        <div className="activity-tags">
                          {activity.inclusions.slice(0, 4).map((inc) => (
                            <span key={inc}>{inc}</span>
                          ))}
                        </div>
                      )}

                    <Link to={`/activities/${activity.id}`} className="activity-book-btn">
                        Book Now <Icon name="arrow" />
                    </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ===== DEPARTING FROM ===== */}
        <section className="section-pad bg-white">
          <div className="container">
            <div style={{ marginBottom: 44 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                Locations
              </span>
              <h2 className="section-title centered">Departing From</h2>
              <p className="section-sub centered">
                We organize transfers from all major cities in the region.
              </p>
            </div>
            <div className="location-grid">
              {departureLocations.map((loc) => (
                <div className="location-card" key={loc.name}>
                  <div className="loc-icon">
                    <Icon name={loc.mode === 'plane' ? 'plane' : 'car'} />
                  </div>
                  <div>
                    <div className="loc-name">{loc.name}</div>
                    <div className="loc-time">{loc.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== CABIN CATEGORIES ===== */}
        <section className="section-pad bg-cream">
          <div className="container">
            <div style={{ marginBottom: 44 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                Choosing a Cabin
              </span>
              <h2 className="section-title centered">Four Categories, One Right Answer for You</h2>
            </div>
            <div className="cabin-grid">
              {cabinTiers.map((c) => (
                <div className="cabin-card" key={c.name}>
                  <div className="ci-icon"><Icon name={c.icon} /></div>
                  <h3>{c.name}</h3>
                  <p>{c.description}</p>
                  <div className="best-for">{c.bestFor}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== PROCESS ===== */}
        <section className="section-pad bg-white">
          <div className="container">
            <div style={{ marginBottom: 52 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                How It Works
              </span>
              <h2 className="section-title centered">From First Message to Wheels Up</h2>
            </div>
            <div className="rituals-rail">
              {steps.map((s) => (
                <div className="ritual-card" key={s.n}>
                  <div className="ritual-num">{s.n}</div>
                  <h3>{s.title}</h3>
                  <p>{s.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== FAQ ===== */}
        <section className="section-pad bg-cream">
          <div className="container">
            <div style={{ marginBottom: 36 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                Before You Book
              </span>
              <h2 className="section-title centered">Frequently Asked Questions</h2>
            </div>
            <div className="faq-list">
              {faqs.map((f, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div className={`faq-item${isOpen ? ' open' : ''}`} key={f.q}>
                    <button
                      type="button"
                      className="faq-q"
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      aria-expanded={isOpen}
                    >
                      <span>{f.q}</span>
                      <span className="plus" />
                    </button>
                    <div className="faq-a">
                      <p>{f.a}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <DefaultFooter />
      </div>
    </>
  );
}